import type { Perspective } from './perspective'
import { SELF_PERSPECTIVE_KEY } from './perspective'

/**
 * Split pipe-delimited plural forms from the backend.
 * Format: "zero|one|many" (vue-i18n style), e.g. "Peers|Peer|Peers"
 */
export const pickPluralForm = (pipeDelimited: string, count: number): string => {
  const parts = pipeDelimited.split('|').map((p) => p.trim())
  if (parts.length === 1) {
    return parts[0]
  }
  if (count === 0) {
    return parts[0] ?? pipeDelimited
  }
  if (count === 1) {
    return parts[1] ?? parts[0] ?? pipeDelimited
  }
  return parts[2] ?? parts[1] ?? parts[0] ?? pipeDelimited
}

export type PerspectiveLabelKind = 'title' | 'nameLong' | 'nameShort'

/**
 * Resolve a perspective key to a display label.
 * Unknown keys must not be dropped — fall back to the raw key.
 * Self has no Perspective object; callers should pass a selfLabel from i18n.
 */
export const resolvePerspectiveLabel = (
  key: string,
  topicPerspectives?: ReadonlyArray<Perspective> | null,
  options?: {
    kind?: PerspectiveLabelKind
    count?: number
    selfLabel?: string
  },
): string => {
  if (key === SELF_PERSPECTIVE_KEY) {
    return options?.selfLabel ?? key
  }

  const perspective = topicPerspectives?.find((p) => p.key === key)

  if (!perspective) {
    return key
  }

  const kind = options?.kind ?? 'nameLong'
  if (kind === 'title') {
    return perspective.title
  }

  const raw = kind === 'nameLong' ? perspective.nameLong : perspective.nameShort

  if (options?.count != null) {
    return pickPluralForm(raw, options.count)
  }
  return pickPluralForm(raw, 2)
}

export const findPerspective = (
  key: string,
  topicPerspectives?: ReadonlyArray<Perspective> | null,
): Perspective | undefined => {
  return topicPerspectives?.find((p) => p.key === key)
}
